import { useDispatch, useSelector, useStore } from "react-redux";
import {
	FootballState,
	PlayInsertionType
} from "@prestosports/prestostats-core/Football";
import {
	applyFilter as applyFilterAction,
	deletePlay as deletePlayAction,
	finalizePlay as finalizePlayAction,
	gameControl as gameControlAction,
	getAllowedTransitions as getAllowedTransitionsAction,
	goBack as goBackAction,
	goForward as goForwardAction,
	initPlaysData as initPlaysDataAction,
	insertNewPlay as insertNewPlayAction,
	insertTimePlay as insertTimePlayAction,
	revertToTransition as revertToTransitionAction,
	saveEditPlay as saveEditPlayAction,
	saveFragment as saveFragmentAction,
	setActiveStep as setActiveStepAction,
	setStartSettings as setStartSettingsAction,
	startGame as startGameAction,
	startOver as startOverAction,
	startPlay as startPlayAction,
	startTransition as startTransitionAction,
	toggleEditPlayMode as toggleEditPlayModeAction,
	toggleInsertPlayMode as toggleInsertPlayModeAction,
	triggerNewPeriodDialog as triggerNewPeriodDialogAction
} from "./actions";
import { getMax } from "../../util/object-util";

const useFootballContext = () => {
	const dispatch = useDispatch();
	const store = useStore();

	const useCurrentPlay = () =>
		useSelector((state) => state.footballPlay.currentPlay);

	const useAllowedTransitions = () =>
		useSelector((state) => state.footballPlay.allowedTransitions);

	const useCanGoBack = () =>
		useSelector((state) => state.footballPlay.canGoBack);

	const useCanGoForward = () =>
		useSelector((state) => state.footballPlay.canGoForward);

	const usePlays = () => useSelector((state) => state.footballPlay.plays);

	const useFilteredPlays = () =>
		useSelector((state) => state.footballPlay.filteredPlays);

	const useSearchText = () =>
		useSelector((state) => state.footballPlay.searchText);

	const usePlayHistory = () =>
		useSelector((state) => state.footballPlay.playHistory);

	const useTransitionHistory = () =>
		useSelector((state) => state.footballPlay.transitionHistory);

	const useMetadata = () => useSelector((state) => state.footballPlay.metadata);

	const useSteps = () => useSelector((state) => state.footballPlay.steps);

	const useActiveStep = () =>
		useSelector((state) => state.footballPlay.activeStep);

	const useLastStep = () =>
		useSelector((state) => {
			const { steps } = state.footballPlay;
			const lastStepIndex = Math.max(...steps.map(({ index }) => index));
			return steps.find(({ index }) => index === lastStepIndex);
		});

	const useCurrentPlayFragments = () =>
		useSelector((state) => {
			return state.footballPlay.currentPlay
				? state.footballPlay.currentPlay.fragments
				: null;
		});

	const useCanChangePeriods = () =>
		useSelector(({ footballPlay }) => {
			return footballPlay.canChangePeriods;
		});

	const useLastPlay = () =>
		useSelector(({ footballPlay }) => {
			return footballPlay.plays.length
				? getMax(footballPlay.plays, "index")
				: null;
		});

	const useIsPatAttempt = () =>
		useSelector(({ footballPlay }) => {
			return (
				footballPlay.currentPlay &&
				footballPlay.currentPlay.fragments.some(
					(f) => f.associatedState === FootballState.PatTry
				)
			);
		});

	const useIsLoading = () =>
		useSelector(({ footballPlay }) => {
			return footballPlay.isLoading;
		});

	const state = {
		plays: usePlays,
		currentPlay: useCurrentPlay,
		allowedTransitions: useAllowedTransitions,
		canGoBack: useCanGoBack,
		canGoForward: useCanGoForward,
		filteredPlays: useFilteredPlays,
		searchText: useSearchText,
		playHistory: usePlayHistory,
		transitionHistory: useTransitionHistory,
		currentPlayFragments: useCurrentPlayFragments,
		metadata: useMetadata,
		canChangePeriods: useCanChangePeriods,
		lastPlay: useLastPlay,
		isPatAttempt: useIsPatAttempt,
		isLoading: useIsLoading,
		// WIZARD
		steps: useSteps,
		activeStep: useActiveStep,
		lastStep: useLastStep
	};

	const goBack = () => {
		const { currentPlay, canGoBack } = store.getState().footballPlay;

		if (canGoBack) {
			goBackAction(currentPlay, dispatch);
		}
	};

	const goForward = () => {
		const { currentPlay, canGoForward } = store.getState().footballPlay;

		if (canGoForward) {
			goForwardAction(currentPlay, dispatch);
		}
	};

	const startPlay = () => {
		const { game } = store.getState().game;

		startPlayAction(game.id, dispatch);
	};

	const startOver = () => {
		const { currentPlay } = store.getState().footballPlay;

		startOverAction(currentPlay, dispatch);
	};

	const gameControl = () => {
		const { game } = store.getState().game;
		const { isTransitioning } = store.getState().footballPlay;

		if (!isTransitioning) {
			gameControlAction(game.id, dispatch);
		}
	};

	const initPlaysData = () => {
		const { game } = store.getState().game;

		initPlaysDataAction(game, dispatch);
	};

	const applyFilter = (searchText) => {
		const { plays } = store.getState().footballPlay;
		const { gameConfig } = store.getState().gameContext;
		applyFilterAction(plays, searchText, gameConfig, dispatch);
	};

	const startTransition = (transitionName, fromHistoryIndex) => {
		const { currentPlay } = store.getState().footballPlay;

		startTransitionAction(
			currentPlay,
			transitionName,
			fromHistoryIndex,
			dispatch
		);
	};

	const revertToTransition = (associatedHistoryIndex) => {
		const { currentPlay } = store.getState().footballPlay;

		revertToTransitionAction(currentPlay, associatedHistoryIndex, dispatch);
	};

	const getAllowedTransitions = (play, playState) => {
		return getAllowedTransitionsAction(play, playState);
	};

	const saveFragment = async (fragment, goToStep = null) => {
		return saveFragmentAction(fragment, goToStep, dispatch);
	};

	const saveEditPlay = async () => {
		const { app, events } = store.getState();
		return saveEditPlayAction(app.editPlay, events.currentEvent.id, dispatch);
	};

	const setActiveStep = (activeStepIndex) => {
		const { currentPlay, steps } = store.getState().footballPlay;
		setActiveStepAction(
			activeStepIndex,
			steps[activeStepIndex],
			currentPlay,
			dispatch
		);
	};

	const goStepBack = () => {
		const { activeStep } = store.getState().footballPlay;
		const newStep = activeStep - 1;
		if (newStep >= 0) {
			setActiveStep(newStep);
		}
	};

	const goStepForward = () => {
		const { activeStep, steps } = store.getState().footballPlay;
		const newStep = activeStep + 1;
		if (newStep < steps.length) {
			setActiveStep(newStep);
		}
	};

	const setStartSettings = (startSettings) => {
		const { game } = store.getState().game;
		setStartSettingsAction(game.id, startSettings, dispatch);
	};

	const startGame = (startSettings) => {
		const { gameId } = store.getState().gameContext;
		startGameAction(gameId, startSettings, dispatch);
	};

	const finalize = () => {
		const { currentPlay } = store.getState().footballPlay;
		const { game } = store.getState().game;
		const { currentEvent } = store.getState().events;

		finalizePlayAction(
			currentPlay.id,
			game.sportCode,
			game.id,
			currentEvent.id,
			dispatch
		);
	};

	const insertNewPlay = () => {
		const { insertPlay, insertType } = store.getState().app;
		const { currentPlay } = store.getState().footballPlay;
		const { game } = store.getState().game;
		const { currentEvent } = store.getState().events;

		insertNewPlayAction(
			currentPlay.id,
			game.id,
			game.sportCode,
			currentEvent.id,
			insertPlay.index,
			insertType,
			dispatch
		);
	};

	const insertTimePlay = ({ seconds, period }) => {
		const { insertPlay, insertType } = store.getState().app;
		const { game } = store.getState().game;
		const { currentEvent } = store.getState().events;

		insertTimePlayAction(
			game.id,
			seconds,
			period,
			game.sportCode,
			currentEvent.id,
			insertPlay.index,
			insertType,
			dispatch
		);
	};

	/**
	 * Determine if the provided milliseconds is within the bounds (inclusive) defined by upperBound and lowerBound.
	 * @param {number} upperBound
	 * @param {number} lowerBound
	 * @param {number} milliseconds
	 * @returns {boolean}
	 * @private
	 */
	// eslint-disable-next-line no-underscore-dangle
	const _validateTime = (upperBound, lowerBound, milliseconds) => {
		return (
			(!upperBound || milliseconds <= upperBound) &&
			(!lowerBound || milliseconds >= lowerBound)
		);
	};

	const isValidTime = (currentPeriod, milliseconds) => {
		const { plays } = store.getState().footballPlay;
		const { editPlay, insertPlay, insertType } = store.getState().app;

		if (Number.isNaN(milliseconds)) {
			return false;
		}

		if (insertPlay) {
			const periodPlays = plays.filter((p) => p.period === insertPlay.period);
			const prevIndex = [PlayInsertionType.Before, PlayInsertionType.Replace].some(
				(s) => s === insertType
			)
				? insertPlay.index - 1
				: insertPlay.index;
			const { time: upperBound } =
				periodPlays.find((p) => p.index === prevIndex) || {};
			const nextIndex =
				insertType === PlayInsertionType.Before
					? insertPlay.index
					: insertPlay.index + 1;
			const { time: lowerBound } =
				periodPlays.find((p) => p.index === nextIndex) || {};

			return _validateTime(upperBound, lowerBound, milliseconds);
		}

		// In edit mode the play being edited (editPlay) is the reference, even if
		// the wizard's currentPlay has drifted to a transient play. Guard on its
		// id: setEditPlay(null) stores {} (truthy), and normal entry must not use
		// this branch.
		if (editPlay?.id) {
			const periodPlays = plays.filter((p) => p.period === editPlay.period);
			const { time: upperBound } =
				periodPlays.find((p) => p.index === editPlay.index - 1) || {};
			// Following plays that merely inherited the clock (no collected time
			// of their own) re-flow to the edited value, so they must not floor
			// it. Bound by the next play that collected an authoritative time.
			// Exclude the edited play itself by id (its own index is not a bound).
			const { time: lowerBound } =
				periodPlays
					.filter(
						(p) => p.index > editPlay.index && p.id !== editPlay.id && p.timeCollected
					)
					.sort((a, b) => a.index - b.index)[0] || {};

			return _validateTime(upperBound, lowerBound, milliseconds);
		}

		if (plays.length) {
			const { time, period } = getMax(plays, "index");
			return currentPeriod >= period && milliseconds <= time;
		}

		return true;
	};

	const deletePlay = (playId) => {
		const { currentPlay } = store.getState().footballPlay;
		const { currentEvent } = store.getState().events;

		deletePlayAction(playId, currentPlay, currentEvent.id, dispatch);
	};

	const toggleEditPlayMode = (play) => {
		if (play) {
			toggleEditPlayModeAction(play.id, play.gameId, dispatch);
		} else {
			const { game } = store.getState().game;
			toggleEditPlayModeAction(null, game.id, dispatch);
		}
	};

	const toggleInsertPlayMode = (
		playId,
		insertType = PlayInsertionType.Before
	) => {
		const { game } = store.getState().game;
		toggleInsertPlayModeAction(playId, insertType, game.id, dispatch);
	};

	const triggerNewPeriodDialog = (trigger) => {
		const { game } = store.getState().game;
		triggerNewPeriodDialogAction(game.id, trigger, dispatch);
	};

	return {
		state,
		goBack,
		goForward,
		startOver,
		startPlay,
		initPlaysData,
		applyFilter,
		startTransition,
		revertToTransition,
		getAllowedTransitions,
		saveFragment,
		saveEditPlay,
		setStartSettings,
		startGame,
		finalize,
		insertNewPlay,
		insertTimePlay,
		isValidTime,
		deletePlay,
		toggleEditPlayMode,
		toggleInsertPlayMode,
		gameControl,
		triggerNewPeriodDialog,
		// WIZARD
		setActiveStep,
		goStepBack,
		goStepForward
	};
};

export { useFootballContext };
